import { useEffect, useRef, useState, useId } from "react";
import {
  ResponsiveContainer, AreaChart, Area, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { Info, AlertTriangle, BarChart3 } from "lucide-react";
import { formatMetric } from "@/lib/dashboardReports.mjs";
import { SEARCH_METRICS, ANALYTICS_METRICS, searchTrendRows, trendAvailable } from "@/lib/dashboardTrends.mjs";


/**
 * Chart palette.
 *
 * Resolved from the CSS custom properties in `src/index.css` rather than typed
 * as hex literals. Roles are fixed:
 *   emerald  the primary observed series
 *   navy     the secondary series
 *   amber    loss or warning only
 *   slate    grid lines, axis ticks, neutral comparison
 */
const chart = () => ({
  primary: "var(--ss-link, #3157f6)",
  secondary: "var(--ss-ink, #141616)",
  light: "var(--ss-muted, #4c5351)",
  grid: "var(--ss-border, #dce0d8)",
  tick: "var(--ss-muted, #4c5351)",
  cursor: "var(--ss-soft, #e8ebe5)",
});

/** Charts sit at the top of the 220-260px band the spec allows. */
const CHART_HEIGHT = 240;

const fmtDate = (d) => {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
const fmtNum = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

const ChartTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-compact border border-line bg-white px-3 py-2 text-xs shadow-overlay">
      <div className="mb-1 font-semibold text-navy-950">{fmtDate(label)}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 text-slate-600">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} aria-hidden="true" />
          <span className="capitalize">{p.name}:</span>
          <span className="tabular font-semibold text-navy-950">{p.value?.toLocaleString()}{unit || ""}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * A chart with no data is not an error, and it is not a blank rectangle either.
 * Say what is missing and when it should arrive.
 */
export function ChartEmpty({ message, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col items-center justify-center rounded-compact bg-offwhite px-5 py-10 text-center"
      style={{ minHeight: CHART_HEIGHT }}
    >
      <BarChart3 size={22} className="text-slate-500" aria-hidden="true" />
      <p className="mt-2 max-w-xs text-small text-slate-600">{message}</p>
    </div>
  );
}

export function ChartCard({ title, explainer, children, testid, right }) {
  return (
    <div data-testid={testid} className="flex min-w-0 flex-col rounded-card border border-line bg-white p-5 md:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-h3 text-navy-950">{title}</h3>
          {explainer && <p className="mt-1 max-w-xl text-small text-slate-600">{explainer}</p>}
        </div>
        {right}
      </div>
      <div className="mt-5 flex-1">{children}</div>
    </div>
  );
}

function SourceTrend({ trend = [], metrics, source, optionalAvailable = true, emptyTestid }) {
  const [selection, setSelection] = useState('combined');
  const gradient = useId().replace(/:/g, '');
  const metric = metrics.find(item => item.key === selection) || metrics[0];
  const available = trendAvailable(trend, metric, optionalAvailable);
  const primary = metric.primary || metric.key;
  const secondaryAxis = source === 'Search Console' && !!metric.secondary;
  const c = chart();
  const label = key => ({ users: 'Active users', sessions: 'Sessions', clicks: 'Clicks', impressions: 'Impressions', ctr: 'CTR', position: 'Average position', engaged_sessions: 'Engaged sessions', engagement_rate: 'Engagement rate', key_events: 'Key events' }[key]);
  return <>
    <div className="ss-chart-options" role="group" aria-label={`${source} chart metric`}>
      {metrics.map(item => <button type="button" key={item.key} aria-pressed={metric.key === item.key} data-testid={`${source === 'Search Console' ? 'gsc' : 'ga4'}-metric-${item.key}`} onClick={() => setSelection(item.key)}>{item.label}</button>)}
    </div>
    <p className="ss-chart-unit">{label(primary)}{metric.suffix ? ' (%)' : ''}{metric.secondary ? ` (solid), ${label(metric.secondary).toLowerCase()} (dashed${secondaryAxis ? ', right axis' : ''})` : ''}{primary === 'position' ? ' · lower is better, aggregated across searches' : ''}</p>
    {!available ? <ChartEmpty testid={emptyTestid} message={metric.optional && !optionalAvailable ? `${source} ${metric.label.toLowerCase()} report unavailable. Other metrics remain separate.` : `No available ${metric.label.toLowerCase()} observations for this range.`} /> : <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <AreaChart data={trend} margin={{ top: 4, right: secondaryAxis ? 0 : 8, left: -12, bottom: 0 }} accessibilityLayer>
        <defs><linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={c.primary} stopOpacity={0.18} /><stop offset="100%" stopColor={c.primary} stopOpacity={0} /></linearGradient></defs>
        <CartesianGrid vertical={false} stroke={c.grid} />
        <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: c.tick }} tickLine={false} axisLine={false} minTickGap={28} />
        <YAxis yAxisId="primary" tickFormatter={fmtNum} tick={{ fontSize: 11, fill: c.tick }} tickLine={false} axisLine={false} width={48} />
        {secondaryAxis && <YAxis yAxisId="secondary" orientation="right" tickFormatter={fmtNum} tick={{ fontSize: 11, fill: c.tick }} tickLine={false} axisLine={false} width={48} />}
        <Tooltip content={<ChartTooltip unit={metric.suffix} />} />
        <Area yAxisId="primary" type="monotone" dataKey={primary} name={label(primary)} stroke={c.primary} strokeWidth={2.4} fill={`url(#${gradient})`} connectNulls={false} isAnimationActive={false} />
        {metric.secondary && <Line yAxisId={secondaryAxis ? 'secondary' : 'primary'} type="monotone" dataKey={metric.secondary} name={label(metric.secondary)} stroke={c.secondary} strokeWidth={1.6} dot={false} strokeDasharray="4 3" connectNulls={false} isAnimationActive={false} />}
      </AreaChart>
    </ResponsiveContainer>}
    {available && <details className="ss-chart-values"><summary>Show daily values</summary><DataTable testid={`${source === 'Search Console' ? 'gsc' : 'ga4'}-daily-values`} rows={trend} columns={[
      { key: 'date', label: 'Date' },
      { key: primary, label: label(primary), align: 'right', render: value => formatMetric(value, metric.suffix) },
      ...(metric.secondary ? [{ key: metric.secondary, label: label(metric.secondary), align: 'right', render: value => formatMetric(value) }] : []),
    ]} /></details>}
  </>;
}
export function ClicksImpressionsTrend({ trend }) {
  return <SourceTrend trend={searchTrendRows(trend || [])} metrics={SEARCH_METRICS} source="Search Console" emptyTestid="gsc-trend-empty" />;
}
export function TrafficTrend({ trend, optionalAvailable = false }) {
  return <SourceTrend trend={trend || []} metrics={ANALYTICS_METRICS} source="GA4" optionalAvailable={optionalAvailable} emptyTestid="ga4-trend-empty" />;
}

export function ConversionBars({ events }) {
  const c = chart();
  if (!events?.length) {
    return <ChartEmpty testid="ga4-conversions-empty" message="No key events recorded in this range. GA4 needs at least one configured key event." />;
  }
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={events} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="event" width={120} tick={{ fontSize: 12, fill: c.secondary }} tickLine={false} axisLine={false} />
        <Tooltip cursor={{ fill: c.cursor }} content={({ active, payload }) => active && payload?.length ? (
          <div className="rounded-compact border border-line bg-white px-3 py-2 text-xs shadow-overlay">
            <span className="tabular font-semibold text-navy-950">{payload[0].payload.event}: {payload[0].value.toLocaleString()}</span>
          </div>
        ) : null} />
        <Bar isAnimationActive={false} dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={26}>
          {/* The leading event takes the primary colour, the rest a quiet tint. */}
          {events.map((e, i) => <Cell key={i} fill={i === 0 ? c.primary : c.light} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Severity.
 *
 * High and medium previously differed only by amber opacity, which is not a
 * distinction anyone can read. Each level now carries a written label as well.
 */
const SEVERITY = {
  // red-700 rather than red-600 for the text: #DC2626 on #FEF2F2 measures
  // 4.41:1, just under AA at this size. Same reason amber text uses amber-700.
  high: { chip: "border-red-600/20 bg-red-50 text-red-700", label: "High" },
  medium: { chip: "border-amber-500/25 bg-amber-50 text-amber-700", label: "Medium" },
  low: { chip: "border-line bg-offwhite text-slate-600", label: "Low" },
};

export function IndexingPanel({ indexing }) {
  if (!indexing || indexing.unavailable || ![indexing.indexed, indexing.not_indexed, indexing.errors].every(value => typeof value === "number" && Number.isFinite(value))) {
    return (
      <div className="flex items-start gap-3 rounded-compact bg-offwhite p-4 text-small text-slate-600">
        <Info size={18} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
        <span>Google doesn't expose bulk index-coverage over the API. Open Search Console's "Pages" report to review indexed vs. excluded URLs directly.</span>
      </div>
    );
  }

  const indexed = indexing?.indexed ?? 0;
  const notIndexed = indexing?.not_indexed ?? 0;
  const errors = indexing?.errors ?? 0;
  const total = indexed + notIndexed + errors;
  const pct = total ? Math.round((indexed / total) * 100) : 0;
  // Guards the bar widths. Dividing by a zero total produced `NaN%`, which
  // browsers drop, collapsing the bar to nothing with no explanation.
  const width = (n) => (total ? `${(n / total) * 100}%` : "0%");

  if (total === 0) {
    return (
      <div data-testid="indexing-empty" className="flex items-start gap-3 rounded-compact bg-offwhite p-4 text-small text-slate-600">
        <Info size={18} className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
        <span>No index-coverage figures for this property yet. They appear once Search Console has crawled the site.</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-end gap-6">
        <div>
          <div className="tabular font-heading text-data text-navy-950">{pct}%</div>
          <div className="text-small text-slate-600">of pages indexed</div>
        </div>
        <div className="grid flex-1 grid-cols-3 gap-2 text-center">
          <Stat n={indexed} label="Indexed" tone="emerald" />
          <Stat n={notIndexed} label="Not indexed" tone="slate" />
          <Stat n={errors} label="Errors" tone="amber" />
        </div>
      </div>
      <div className="mt-4 flex h-2.5 w-full overflow-hidden rounded-full bg-offwhite">
        <div className="h-full bg-emerald-600" style={{ width: width(indexed) }} />
        <div className="h-full bg-line-strong" style={{ width: width(notIndexed) }} />
        <div className="h-full bg-amber-500" style={{ width: width(errors) }} />
      </div>
      {indexing.problems?.length > 0 && (
        <ul className="mt-5 space-y-2">
          {indexing.problems.map((p, i) => {
            const sev = SEVERITY[p.severity] || SEVERITY.low;
            return (
              <li key={i} className="flex items-center justify-between gap-3 rounded-compact border border-line px-3 py-2">
                <span className="truncate font-mono text-xs text-navy-800" title={p.url}>{p.url}</span>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${sev.chip}`}>
                  {sev.label} · {p.issue}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const toneMap = {
  emerald: "text-emerald-700",
  amber: "text-amber-700",
  slate: "text-slate-600",
};
function Stat({ n, label, tone }) {
  return (
    <div className="rounded-compact bg-offwhite py-2">
      <div className={`tabular font-heading text-xl font-bold ${toneMap[tone]}`}>{n}</div>
      <div className="text-[12px] font-medium text-slate-600">{label}</div>
    </div>
  );
}

/**
 * Data table.
 *
 * Three behaviours worth knowing about:
 *
 * 1. Numeric cells use tabular figures whether or not they are right-aligned,
 *    so a column of positions does not shift as digits change.
 * 2. The header sticks while the body scrolls inside the card, so a long table
 *    never leaves you scrolling blind.
 * 3. When the table is wider than its container, a right-hand fade appears.
 *    Without it, a mobile reader has no way to know a column is hidden.
 *
 * Column options: `key`, `label`, `align`, `numeric`, `mono`, `wide`, `render`.
 */
export function DataTable({ columns, rows, testid, emptyMessage = "Nothing to show for this range yet." }) {
  const scrollerRef = useRef(null);
  const [overflowing, setOverflowing] = useState(false);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => {
      const scrollable = el.scrollWidth - el.clientWidth;
      setOverflowing(scrollable > 4);
      setAtEnd(scrollable <= 4 || el.scrollLeft >= scrollable - 4);
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => { el.removeEventListener("scroll", measure); ro?.disconnect(); };
  }, [rows, columns]);

  if (!rows?.length) {
    return (
      <div data-testid={testid} className="rounded-compact bg-offwhite px-4 py-8 text-center text-small text-slate-600">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="relative">
      <div ref={scrollerRef} data-testid={testid} className="max-h-[360px] overflow-auto" tabIndex={0} role="region" aria-label="Scrollable report table">
        <table className="ss-report-table w-full text-table-sm">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="border-b border-line text-left">
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={`bg-white pb-2.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-600 ${
                    c.align === "right" ? "ss-report-number text-right" : ""
                  } ${c.wide ? "min-w-[180px]" : "whitespace-nowrap"}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-line/70 transition-colors duration-control last:border-0 hover:bg-offwhite">
                {columns.map((c) => {
                  const raw = r[c.key];
                  const numeric = c.numeric || c.align === "right";
                  return (
                    <td
                      key={c.key}
                      // Keep the original value available without shortening its identity.
                      title={c.mono || c.wide ? String(raw ?? "") : undefined}
                      className={`py-2.5 ${c.align === "right" ? "text-right" : ""} ${
                        numeric ? "ss-report-number tabular" : ""
                      } ${c.mono ? "ss-report-text font-mono text-xs text-navy-800" : c.wide ? "ss-report-text text-navy-800" : "text-navy-800"}`}
                    >
                      {c.render ? c.render(raw, r) : raw ?? "Unavailable"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {overflowing && !atEnd && (
        <div
          aria-hidden="true"
          data-testid="table-scroll-cue"
          className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent"
        />
      )}
    </div>
  );
}

/** Shared error state for any chart or table block that failed to load. */
export function DataError({ message, onRetry, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center rounded-compact bg-offwhite px-5 py-8 text-center">
      <AlertTriangle size={22} className="text-amber-700" aria-hidden="true" />
      <p className="mt-2 max-w-sm text-small text-slate-600">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 rounded-btn border border-line-strong bg-white px-3 py-2 text-small font-semibold text-navy-950 transition-colors duration-control hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
        >
          Try again
        </button>
      )}
    </div>
  );
}
